import useUploadDocumentDialogViewModel from './UploadDocumentDialogViewModel'
import { ORG_DOCUMENT_STATUS_MODEL_APPLIED, PURCHASE_TYPE_PROCESS_DOCUMENT } from '@constants'
import cn from 'clsx'

import Dialog from '@components/shared/Dialogs/Dialog'
import CircleSpinner from '@components/shared/CircleSpinner'
import TextArea from '@components/shared/TextArea'
import PaymentRequiredFlow from '@components/shared/Dialogs/PaymentRequiredDialog/PaymentRequiredFlow'
import UDDCheckboxControls from '@components/shared/Dialogs/UploadDocumentDialog/widgets/UDDCheckboxControls'
import UDDFileDetailsRow from '@components/shared/Dialogs/UploadDocumentDialog/widgets/UDDFileDetailsRow'
import UDDFooter from '@components/shared/Dialogs/UploadDocumentDialog/widgets/UDDFooter'
import Checkbox from '$components/Forms/Checkbox'

const UPLOAD_STEPS = {
    PRE_UPLOAD: 'pre_upload',
    UPLOADING: 'uploading',
}

const UploadDocumentDialog = ({ 
    open, 
    setOpen,
    files,
    collectionId, 
    setUpdateDocs, 
    runsheetId,
    runsheetTabId,
}) => {
    
    const vm = useUploadDocumentDialogViewModel({
        open,
        setOpen,
        files,
        collectionId,
        runsheetId,
        runsheetTabId,
        setUpdateDocs,
    })
    
    return (
        
        <Dialog
            className={cn('UploadDocumentDialog relative')}
            panelClassName="min-w-[650px] max-[650px] modal-content"
            open={[UPLOAD_STEPS.PRE_UPLOAD, UPLOAD_STEPS.UPLOADING].includes(vm.uploadStep)}
            setOpen={setOpen}
            modal={false}
            title={(
                <header className="flex justify-between items-end gap-4 text-lg font-bold">
                    {vm.modalTitle}
                </header>
            )}
            positiveText={vm.uploadStep === UPLOAD_STEPS.UPLOADING ? 'OK' : ''}
            footers={vm.uploadStep !== UPLOAD_STEPS.UPLOADING ? (
                <UDDFooter
                    isUploadStep={vm.uploadStep === UPLOAD_STEPS.UPLOADING}
                    batchUploadDocuments={vm.batchUploadDocuments}
                    shouldProcessDocs={vm.shouldProcessDocs}
                    setShouldProcessDocs={vm.setShouldProcessDocs}
                    validUploadData={vm.validUploadData}
                    hasInsufficientBalance={vm.hasInsufficientBalance}
                    paymentsEnabled={vm.payments.enabled}
                    toggleUploadDocumentDialogOpen={vm.toggleUploadDocumentDialogOpen} />
            ) : null}>
            
            {vm.uploadStep === UPLOAD_STEPS.UPLOADING && (
                <div className="mb-4 modal-body">
                    <p>Your files are uploading.</p>
                    <p className="mt-3">
                        You can continue using the app while the uploads are in progress,
                        but do not navigate away or refresh the page until the uploads are complete.
                    </p>
                    <p>
                        <label className="flex items-center content-center gap-2 mt-3 pl-1">
                            <Checkbox
                                checked={vm.skipUploadHelperInfoDialog}
                                onChange={e => vm.setSkipUploadHelperInfoDialog(e.target.checked)} />
                            <span>Don't show this again</span>
                        </label>
                    </p>
                </div>
            )}
            
            {vm.uploadStep !== UPLOAD_STEPS.UPLOADING && (
                
                <div className="modal-body">
                    
                    <div className="max-h-72 mb-2 px-2 overflow-auto" data-test-id="UploadDocumentPopUpFileNames">
                        {vm.uploadData.length > 0 ?
                            <table className="table table-auto table-sm table-zebra table-pin-rows">
                                <thead>
                                    <UDDCheckboxControls
                                        enabledFiles={vm.enabledFiles}
                                        validUploadData={vm.validUploadData}
                                        setValidUploadData={vm.setValidUploadData} />
                                </thead>
                                <tbody>
                                    {vm.uploadData.map((it, i) => (
                                        <UDDFileDetailsRow
                                            key={`upload-${i}-${it.file.name}`}
                                            item={it}
                                            fileIsWarning={vm.fileIsWarning}
                                            fileIsError={vm.fileIsError}
                                            fileValidations={vm.fileValidations}
                                            handleWithTooltipMessage={vm.handleWithTooltipMessage}
                                            handleWithValidUploadData={vm.handleWithValidUploadData} />
                                    ))}
                                </tbody>
                            </table>
                            : <CircleSpinner className="flex justify-center" />}
                    </div>
                    
                    {vm.isSingle && vm.validUploadData.length > 0 && (
                        <div className="mb-4">
                            <label htmlFor="fileUploadFileDescriptionInput">Description</label>
                            <div className="w-75 mt-1">
                                <TextArea
                                    className="textarea textarea-bordered w-full"
                                    name="description"
                                    id="fileUploadFileDescriptionInput"
                                    placeholder="Detailed description"
                                    onChange={e => vm.setDescription(e.target.value)}
                                    value={vm.description} />
                            </div>
                        </div>
                    )}
                    
                    {vm.shouldProcessDocs && vm.showPaymentInnerModal && (
                        <div className="PaymentRequiredDialog max-w-full mt-4">
                            <PaymentRequiredFlow
                                payments={vm.payments}
                                purchaseType={PURCHASE_TYPE_PROCESS_DOCUMENT}
                                itemCount={vm.validUploadData?.length}
                                pastPurchases={vm.validUploadData?.filter(
                                    ud => ud.orgStatus === ORG_DOCUMENT_STATUS_MODEL_APPLIED).length}
                                onInsufficientBalanceChange={vm.setHasInsufficientBalance} />
                        </div>
                    )}
                
                </div>
                
            )}
        
        </Dialog>
        
    )
    
}

export default UploadDocumentDialog
